// generateYuklemeCetvelMultiFloor.js
// Çok Katlı Tek Sayaçlı Projeler için Yükleme Cetveli
import { Rectangle, Line, Text } from "./utils";

const layers = {
  0: { name: '0', frozen: false, visible: true, colorIndex: 7, color: 16777215 },
  kirmizi: { name: '18', frozen: false, visible: true, colorIndex: 1, color: 16711680 },
  yesil: { name: 'test', frozen: false, visible: true, colorIndex: 1, color: 65280 },
  mavi: { name: 'test', frozen: false, visible: true, colorIndex: 1, color: 255 },
  sari: { name: 'test', frozen: false, visible: true, colorIndex: 1, color: 16776960 },
  magenta: { name: 'test', frozen: false, visible: true, colorIndex: 1, color: 16711935 },
  cyan: { name: 'test', frozen: false, visible: true, colorIndex: 1, color: 65535 },
  gray: { name: 'test', frozen: false, visible: true, colorIndex: 8, color: 12632256 },
  AKSYAZI: { name: 'AKSYAZI', frozen: false, visible: true, colorIndex: 9, color: 12632256 }
};

const gray = layers.gray;

const op_gray = {
  color: gray.color,
  colorIndex: gray.colorIndex,
  layer: gray.name,
  shape: true,
  hasContinuousLinetypePattern: false
};

// Ölçek faktörü (eski Yükleme Cetveli ile aynı)
const Scl = 0.5;
const rowHeight = 50 * Scl;

// Toplam tablo genişliği (eski Yükleme Cetveli ile aynı: 2100 * Scl = 1050)
const TOTAL_WIDTH = 2100 * Scl;

// Kolon genişlikleri (TOTAL_WIDTH'e orantılı olarak ayarlandı)
const COL = {
  ADI: 140 * Scl,
  KURULU_GUC: 180 * Scl,
  TALEP_GUCU: 180 * Scl,
  KABLO: 200 * Scl,
  TALEP_FAK: 140 * Scl,
  SIGORTA_TABLO: 160 * Scl,
  LINYE_NO: 120 * Scl,
  SIGORTA_Io: 160 * Scl,
  SIGORTA_Ith: 120 * Scl,
  SORTI_ISIK: 110 * Scl,
  SORTI_PRIZ: 110 * Scl,
  GUC: 160 * Scl,
  ACIKLAMALAR: 320 * Scl,
};

/**
 * Çok katlı tek sayaçlı projeler için Yükleme Cetveli oluşturur
 * @param {number} X - Başlangıç X koordinatı
 * @param {number} Y - Başlangıç Y koordinatı (üst kenar)
 * @param {number} W - Genişlik (kullanılmıyor, sabit genişlik)
 * @param {number} H - Yükseklik (kullanılmıyor, dinamik)
 * @param {Object} data - Panel verileri { panels: [], adp: {} }
 * @returns {Object} - { result: [], width, height }
 */
export const createYuklemeCetvelMultiFloor = (X, Y, W, H, data) => {
  let result = [];
  const option = op_gray;
  const gap = 10 * Scl;

  // Data güvenliği
  const panels = data?.panels || [];
  const adpNode = data?.adp;

  // Başlangıç pozisyonları
  let currentY = Y;
  const startX = X + gap;

  // 1. Başlık satırı - "YÜKLEME CETVELİ"
  result.push(...createTitle(startX, currentY, option));
  currentY -= 60 * Scl;

  // 2. Ana header satırı (TABLO, LİNYE NO, SİGORTA, SORTİ, GÜÇ, AÇIKLAMALAR)
  result.push(...createMainHeader(startX, currentY, option));
  currentY -= rowHeight * 2;

  // 3. Her panel için tablo oluştur
  panels.forEach((panel) => {
    const branchCircuits = panel?.branchCircuits || [];
    const rowCount = Math.max(branchCircuits.length, 1);

    result.push(...createPanelTable(startX, currentY, panel, option));
    currentY -= rowCount * rowHeight;
  });

  // 4. Alt özet tablosu (üstteki panel tablolarından boşluk bırak)
  currentY -= gap * 3; // Panel tabloları ile özet tablosu arasında boşluk
  result.push(...createSummaryTable(startX, currentY, panels, adpNode, option));
  const summaryHeight = (panels.length + 2) * rowHeight;
  currentY -= summaryHeight;

  // 5. Dış çerçeve
  const totalHeight = Y - currentY + gap;
  result.push(Rectangle(X, Y + gap, TOTAL_WIDTH + 2 * gap, -(totalHeight + gap), option));

  return {
    result: result,
    width: TOTAL_WIDTH + 2 * gap,
    height: totalHeight + gap
  };
};

/**
 * Başlık oluşturur - "YÜKLEME CETVELİ"
 */
function createTitle(X, Y, option) {
  const result = [];
  const textHeight = 40 * Scl;

  const text = {
    text: "YÜKLEME CETVELİ",
    x: X + TOTAL_WIDTH / 2 - 120 * Scl,
    y: Y - 40 * Scl,
    textHeight: textHeight
  };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  result.push(Rectangle(X, Y, TOTAL_WIDTH, -60 * Scl, option));

  return result;
}

/**
 * Ana header satırını oluşturur
 */
function createMainHeader(X, Y, option) {
  const result = [];
  const textHeight = 18 * Scl;
  const smallTextHeight = 14 * Scl;
  let X1 = X;

  // Ana çerçeve
  result.push(Rectangle(X, Y, TOTAL_WIDTH, -rowHeight * 2, option));

  // TABLO başlığı (üst satır - birleştirilmiş)
  const tabloWidth = COL.ADI + COL.KURULU_GUC + COL.TALEP_GUCU + COL.KABLO + COL.TALEP_FAK + COL.SIGORTA_TABLO;
  let text = { text: "TABLO", x: X1 + tabloWidth / 2 - 30 * Scl, y: Y - 20 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  result.push(Line(X1, Y - rowHeight, X1 + tabloWidth, Y - rowHeight, option));

  // TABLO alt başlıkları
  // ADI
  text = { text: "ADI", x: X1 + 15 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.ADI;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // KURULU GÜÇ (W)
  text = { text: "KURULU", x: X1 + 10 * Scl, y: Y - rowHeight - 12 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  text = { text: "GÜÇ (W)", x: X1 + 10 * Scl, y: Y - rowHeight - 28 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.KURULU_GUC;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // TALEP GÜCÜ (W)
  text = { text: "TALEP", x: X1 + 15 * Scl, y: Y - rowHeight - 12 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  text = { text: "GÜCÜ (W)", x: X1 + 5 * Scl, y: Y - rowHeight - 28 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.TALEP_GUCU;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // KABLO
  text = { text: "KABLO", x: X1 + 20 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.KABLO;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // TALEP FAK(%)
  text = { text: "TALEP", x: X1 + 10 * Scl, y: Y - rowHeight - 12 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  text = { text: "FAK(%)", x: X1 + 5 * Scl, y: Y - rowHeight - 28 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.TALEP_FAK;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // SİGORTA (A) - Tablo
  text = { text: "SİGORTA", x: X1 + 5 * Scl, y: Y - rowHeight - 12 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  text = { text: "(A)", x: X1 + 20 * Scl, y: Y - rowHeight - 28 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.SIGORTA_TABLO;

  // TABLO bölümü dikey çizgisi
  result.push(Line(X1, Y, X1, Y - rowHeight * 2, option));

  // LİNYE NO
  text = { text: "LİNYE", x: X1 + 10 * Scl, y: Y - 15 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  text = { text: "NO", x: X1 + 15 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.LINYE_NO;
  result.push(Line(X1, Y, X1, Y - rowHeight * 2, option));

  // SİGORTA (A) üst başlık
  const sigortaWidth = COL.SIGORTA_Io + COL.SIGORTA_Ith;
  text = { text: "SİGORTA (A)", x: X1 + 10 * Scl, y: Y - 15 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  result.push(Line(X1, Y - rowHeight, X1 + sigortaWidth, Y - rowHeight, option));

  // Io
  text = { text: "Io", x: X1 + 25 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.SIGORTA_Io;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // Ith
  text = { text: "Ith", x: X1 + 15 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.SIGORTA_Ith;
  result.push(Line(X1, Y, X1, Y - rowHeight * 2, option));

  // SORTİ üst başlık
  const sortiWidth = COL.SORTI_ISIK + COL.SORTI_PRIZ;
  text = { text: "SORTİ", x: X1 + 20 * Scl, y: Y - 15 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  result.push(Line(X1, Y - rowHeight, X1 + sortiWidth, Y - rowHeight, option));

  // IŞIK
  text = { text: "IŞIK", x: X1 + 10 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.SORTI_ISIK;
  result.push(Line(X1, Y - rowHeight, X1, Y - rowHeight * 2, option));

  // PRİZ
  text = { text: "PRİZ", x: X1 + 10 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.SORTI_PRIZ;
  result.push(Line(X1, Y, X1, Y - rowHeight * 2, option));

  // GÜÇ (W)
  text = { text: "GÜÇ (W)", x: X1 + 10 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.GUC;
  result.push(Line(X1, Y, X1, Y - rowHeight * 2, option));

  // AÇIKLAMALAR
  text = { text: "AÇIKLAMALAR", x: X1 + 30 * Scl, y: Y - rowHeight - 20 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));

  return result;
}

/**
 * Panel tablosu oluşturur (Z.K.T., N.K.T.-1, vb.)
 */
function createPanelTable(X, Y, panel, option) {
  const result = [];
  const textHeight = 14 * Scl;
  const branchCircuits = panel?.branchCircuits || [];
  const rowCount = Math.max(branchCircuits.length, 1);
  const tableHeight = rowCount * rowHeight;

  let X1 = X;

  // Sol taraf - Panel bilgileri (dikey birleştirilmiş hücreler)
  // ADI
  let text = { text: panel.name || "TABLO", x: X1 + 5 * Scl, y: Y - tableHeight / 2 - 5 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.ADI;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // KURULU GÜÇ
  text = { text: String(panel.installedPower || 0), x: X1 + 10 * Scl, y: Y - tableHeight / 2 - 5 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.KURULU_GUC;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // TALEP GÜCÜ
  text = { text: String(panel.demandPower || 0), x: X1 + 10 * Scl, y: Y - tableHeight / 2 - 5 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.TALEP_GUCU;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // KABLO
  const cableText = panel.cableTitle || "5X6 mm²\nNYM-J";
  text = { text: cableText, x: X1 + 5 * Scl, y: Y - tableHeight / 2 - 5 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.KABLO;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // TALEP FAK
  text = { text: String(panel.demandFactor || "60-40"), x: X1 + 10 * Scl, y: Y - tableHeight / 2 - 5 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.TALEP_FAK;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // SİGORTA (Tablo)
  const breakerTitle = panel.panelMainBreaker ?
    `${panel.panelMainBreaker.polar || 4}x${panel.panelMainBreaker.curve || 'B'}-${panel.panelMainBreaker.current || 32}` :
    "4xB-32";
  text = { text: breakerTitle, x: X1 + 5 * Scl, y: Y - tableHeight / 2 - 5 * Scl, textHeight: textHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += COL.SIGORTA_TABLO;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // Sağ taraf - Branch circuits
  branchCircuits.forEach((circuit, i) => {
    const rowY = Y - (i + 1) * rowHeight;
    let colX = X1;

    // LİNYE NO
    text = { text: String(i + 1), x: colX + 20 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    colX += COL.LINYE_NO;

    // SİGORTA Io
    const breaker = circuit.breaker || {};
    const ioText = `${breaker.curve || 'B'}-${breaker.current || 10}A`;
    text = { text: ioText, x: colX + 5 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    colX += COL.SIGORTA_Io;

    // SİGORTA Ith
    const ithText = breaker.shortCircuitCurCap ? `${breaker.shortCircuitCurCap}kA` : "3kA";
    text = { text: ithText, x: colX + 5 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    colX += COL.SIGORTA_Ith;

    // SORTİ IŞIK
    const lightCount = circuit.type === "light" ? (circuit.sorti || 0) : "";
    text = { text: String(lightCount), x: colX + 15 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    colX += COL.SORTI_ISIK;

    // SORTİ PRİZ
    const socketCount = (circuit.type === "socket" || circuit.type === "socketOven" ||
                        circuit.type === "socketDish" || circuit.type === "socketWash") ?
                        (circuit.sorti || 0) : "";
    text = { text: String(socketCount), x: colX + 15 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    colX += COL.SORTI_PRIZ;

    // GÜÇ
    text = { text: String(circuit.totalPower || 0), x: colX + 10 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    colX += COL.GUC;

    // AÇIKLAMALAR
    text = { text: circuit.description || getBranchDescription(circuit.type), x: colX + 5 * Scl, y: rowY + 12 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));

    // Satır çizgisi
    if (i < branchCircuits.length - 1) {
      result.push(Line(X1, rowY, X + TOTAL_WIDTH, rowY, option));
    }
  });

  // Dikey çizgiler (sağ taraf kolonları)
  let lineX = X1 + COL.LINYE_NO;
  result.push(Line(lineX, Y, lineX, Y - tableHeight, option));
  lineX += COL.SIGORTA_Io;
  result.push(Line(lineX, Y, lineX, Y - tableHeight, option));
  lineX += COL.SIGORTA_Ith;
  result.push(Line(lineX, Y, lineX, Y - tableHeight, option));
  lineX += COL.SORTI_ISIK;
  result.push(Line(lineX, Y, lineX, Y - tableHeight, option));
  lineX += COL.SORTI_PRIZ;
  result.push(Line(lineX, Y, lineX, Y - tableHeight, option));
  lineX += COL.GUC;
  result.push(Line(lineX, Y, lineX, Y - tableHeight, option));

  // Dış çerçeve
  result.push(Rectangle(X, Y, TOTAL_WIDTH, -tableHeight, option));

  return result;
}

/**
 * Alt özet tablosu oluşturur
 */
function createSummaryTable(X, Y, panels, adpNode, option) {
  const result = [];
  const textHeight = 14 * Scl;
  const smallTextHeight = 12 * Scl;

  // Özet tablosu genişliği = Panel tablosu genişliği (TOTAL_WIDTH)
  const summaryWidth = TOTAL_WIDTH;

  // Kolon genişliklerini TOTAL_WIDTH'e göre orantılı hesapla
  const baseWidth = 870 * Scl; // Orijinal sumCols toplamı
  const scale = summaryWidth / baseWidth;

  const sumCols = {
    name: 120 * Scl * scale,
    kuruluGuc: 100 * Scl * scale,
    talepFak: 80 * Scl * scale,
    talepGuc: 100 * Scl * scale,
    akim: 80 * Scl * scale,
    akimTasKap: 80 * Scl * scale,
    kolonHatti: 130 * Scl * scale,
    kesiciSayac: 90 * Scl * scale,
    kesiciKatPano: 90 * Scl * scale,
  };

  const rowCount = panels.length + 2; // panels + header + ADP total row
  const tableHeight = rowCount * rowHeight;

  let X1 = X;
  let currentY = Y;

  // Header satırı
  result.push(Rectangle(X, Y, summaryWidth, -rowHeight, option));

  // Header metinleri
  let text = { text: "", x: X1 + 10 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.name;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  text = { text: "Kurulu Güç (W)", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.kuruluGuc;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  text = { text: "Talep Fak. (%)", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.talepFak;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  text = { text: "Talep Güç(W)", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.talepGuc;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  text = { text: "Akım (A)", x: X1 + 10 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.akim;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  text = { text: "Akım Taş. Kap.", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.akimTasKap;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  text = { text: "Kolon Hattı", x: X1 + 20 * Scl, y: currentY - 18 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.kolonHatti;
  result.push(Line(X1, Y, X1, Y - tableHeight, option));

  // Kesici başlığı (birleşik)
  text = { text: "Kesici", x: X1 + 50 * Scl, y: currentY - 10 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  result.push(Line(X1, Y - rowHeight / 2, X1 + sumCols.kesiciSayac + sumCols.kesiciKatPano, Y - rowHeight / 2, option));

  text = { text: "Sayaç", x: X1 + 20 * Scl, y: currentY - rowHeight / 2 - 12 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));
  X1 += sumCols.kesiciSayac;
  result.push(Line(X1, Y - rowHeight / 2, X1, Y - tableHeight, option));

  text = { text: "Kat Pano", x: X1 + 10 * Scl, y: currentY - rowHeight / 2 - 12 * Scl, textHeight: smallTextHeight };
  text.handle = generateStableId(text);
  result.push(Text(text.x, text.y, text, 0, option));

  // Panel satırları
  currentY -= rowHeight;

  // Panelleri ters sırada göster (Z.K.T. en alta gelecek şekilde)
  const sortedPanels = [...panels].reverse();

  sortedPanels.forEach((panel, i) => {
    result.push(Line(X, currentY, X + summaryWidth, currentY, option));

    X1 = X;
    const current = panel.demandPower ? (panel.demandPower / 526).toFixed(2) : "0";
    const breakerTitle = panel.panelMainBreaker ?
      `${panel.panelMainBreaker.curve || 'B'}-${panel.panelMainBreaker.polar || 4}x${panel.panelMainBreaker.current || 40}A` :
      "B-4x40A";

    // Panel adı
    text = { text: panel.name || "PANEL", x: X1 + 10 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.name;

    // Kurulu Güç
    text = { text: `${panel.installedPower || 0}   W`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.kuruluGuc;

    // Talep Fak
    text = { text: String(panel.demandFactor || "60-40"), x: X1 + 10 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.talepFak;

    // Talep Güç
    text = { text: `${panel.demandPower || 0}   W`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.talepGuc;

    // Akım
    text = { text: `${current}   A`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.akim;

    // Akım Taş. Kap.
    text = { text: `< ${panel.cableCurrentLimit || 43}   A`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.akimTasKap;

    // Kolon Hattı
    text = { text: panel.cableTitle || "5X6 mm² NYM-J", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.kolonHatti;

    // Kesici Sayaç (boş - sadece A.D.P. satırında dolu)
    X1 += sumCols.kesiciSayac;

    // Kesici Kat Pano
    text = { text: breakerTitle, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));

    currentY -= rowHeight;
  });

  // A.D.P. (Sayaç Pano) toplam satırı
  if (adpNode) {
    result.push(Line(X, currentY, X + summaryWidth, currentY, option));

    X1 = X;
    const adpCurrent = adpNode.current || (adpNode.demandPower / 526).toFixed(2);
    const adpBreakerTitle = adpNode.breaker ?
      `${adpNode.breaker.curve || 'C'}-${adpNode.breaker.polar || 4}x${adpNode.breaker.current || 63}A` :
      "C-4x63A";

    // A.D.P. adı
    text = { text: "A.D.P. (Sayaç Pano)", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.name;

    // Toplam Kurulu Güç
    text = { text: `${adpNode.installedTotalPower || 0}   W`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.kuruluGuc;

    // Toplam Talep Fak
    text = { text: "60-40", x: X1 + 10 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.talepFak;

    // Toplam Talep Güç
    text = { text: `${adpNode.demandPower || 0}   W`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.talepGuc;

    // Toplam Akım
    text = { text: `${adpCurrent}   A`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.akim;

    // Akım Taş. Kap.
    text = { text: `< ${adpNode.cableCurrentLimit || 80}   A`, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.akimTasKap;

    // Ana Kolon Hattı
    text = { text: adpNode.cableTitle || "4X16 mm² NYM-J", x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.kolonHatti;

    // Kesici Sayaç
    text = { text: adpBreakerTitle, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
    X1 += sumCols.kesiciSayac;

    // Kesici Kat Pano (Ana kesici)
    const panelBreakerTitle = adpNode.breaker ?
      `${adpNode.breaker.curve || 'B'}-${adpNode.breaker.polar || 4}x${adpNode.breaker.current || 63}A` :
      "B-4x63A";
    text = { text: panelBreakerTitle, x: X1 + 5 * Scl, y: currentY - 18 * Scl, textHeight: textHeight };
    text.handle = generateStableId(text);
    result.push(Text(text.x, text.y, text, 0, option));
  }

  // Dış çerçeve
  result.push(Rectangle(X, Y, summaryWidth, -tableHeight, option));

  return result;
}

/**
 * Branch circuit tipine göre açıklama döndürür
 */
function getBranchDescription(type) {
  const descriptions = {
    light: "AYDINLATMA LİNYESİ",
    socket: "PRİZ LİNYESİ",
    socketOven: "FIRIN",
    socketDish: "BULAŞIK MAKİNASI",
    socketWash: "ÇAMAŞIR MAKİNASI",
    branchSocket: "PRİZ LİNYESİ",
    branchSocketMachine: "MAKİNE PRİZİ",
    branchLight: "AYDINLATMA LİNYESİ",
    hidrofor: "HİDROFOR",
    interkom: "İNTERKON",
    klimaDis: "KLİMA DIŞ ÜNİTE",
  };
  return descriptions[type] || "";
}

/**
 * Stable ID oluşturur
 */
function generateStableId(element) {
  const faktor = element.handFak || "0";
  return `text-${element.text}-${element.x}-${element.y}-${element.textHeight}-${faktor}`;
}

export default createYuklemeCetvelMultiFloor;
